import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Spin, List, Typography, Drawer, Tabs, Empty, Tooltip, Collapse, Modal, Popconfirm, Divider } from 'antd';
import {
    MessageOutlined, 
    SendOutlined, 
    CloseOutlined,
    HistoryOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    RobotOutlined,
    ExpandOutlined,
    PlusOutlined,
    DeleteOutlined,
    CommentOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/ApiService';
import './ai-chat.css';

const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

export default function AiChatPanel({ visible, onClose }) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [activeTab, setActiveTab] = useState('chat');
    const [fullscreenModal, setFullscreenModal] = useState({ visible: false, html: '', title: '' });
    const messagesEndRef = useRef(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        if (visible) {
            loadSessions();
        }
    }, [visible]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadSessions = async () => {
        const res = await ApiService.AiChat.GetSessions();
        if (res.success) {
            setSessions(res.data);
        }
    };

    const loadSessionMessages = async (sessionId) => {
        const res = await ApiService.AiChat.GetSessionMessages(sessionId);
        if (res.success) {
            const formattedMessages = [];
            res.data.forEach(msg => {
                if (msg.role === 'user') {
                    formattedMessages.push({
                        type: 'user',
                        content: msg.question,
                        timestamp: new Date(msg.createdDate)
                    });
                }
                // Model yanıtı aynı history kaydında
                if (msg.sqlQuery || msg.htmlTemplate || msg.errorMessage) {
                    formattedMessages.push({
                        type: 'ai',
                        content: msg,
                        isSuccessful: msg.isSuccessful,
                        timestamp: new Date(msg.createdDate)
                    });
                }
            });
            setMessages(formattedMessages);
        }
    };

    const handleNewSession = async () => {
        const res = await ApiService.AiChat.CreateSession();
        if (res.success) {
            setCurrentSessionId(res.data.id);
            setMessages([]);
            loadSessions();
            setActiveTab('chat');
        }
    };

    const handleDeleteSession = async (sessionId, e) => {
        e?.stopPropagation();
        const res = await ApiService.AiChat.DeleteSession(sessionId);
        if (res.success) {
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                setMessages([]);
            }
            loadSessions();
        }
    };

    const handleSelectSession = async (session) => {
        setCurrentSessionId(session.id);
        await loadSessionMessages(session.id);
        setActiveTab('chat');
    };

    const handleSend = async () => {
        if (!inputValue.trim() || loading) return;

        const userMessage = {
            type: 'user',
            content: inputValue,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const question = inputValue;
        setInputValue('');
        setLoading(true);

        try {
            const res = await ApiService.AiChat.Ask({ 
                question: question,
                sessionId: currentSessionId
            });
            
            if (res.success && res.data?.sessionId) {
                // Yeni session oluşturulduysa güncelle
                if (!currentSessionId || currentSessionId !== res.data.sessionId) {
                    setCurrentSessionId(res.data.sessionId);
                    loadSessions();
                }
            }

            const aiMessage = {
                type: 'ai',
                content: res.success ? res.data : { errorMessage: res.message || 'Bir hata oluştu' },
                isSuccessful: res.success && res.data?.isSuccessful,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            const errorMessage = {
                type: 'ai',
                content: { errorMessage: 'İstek gönderilemedi' },
                isSuccessful: false,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleRerun = async (chatId) => {
        setLoading(true);
        try {
            const res = await ApiService.AiChat.RerunChat(chatId);
            
            const aiMessage = {
                type: 'ai',
                content: res.success ? res.data : { errorMessage: res.message || 'Bir hata oluştu' },
                isSuccessful: res.success && res.data?.isSuccessful,
                timestamp: new Date(),
                isRerun: true
            };

            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            // handle error
        } finally {
            setLoading(false);
        }
    };

    const renderHtmlResult = (htmlTemplate, resultJson) => {
        if (!htmlTemplate) return null;

        try {
            const data = resultJson ? JSON.parse(resultJson) : [];
            const finalHtml = htmlTemplate.replace('{{DATA}}', JSON.stringify(data));
            
            return (
                <div className="ai-chat-result-container">
                    <div className="ai-chat-result-header">
                        <Button
                            type="text"
                            size="small"
                            icon={<ExpandOutlined />}
                            onClick={() => setFullscreenModal({ visible: true, html: finalHtml, title: 'AI Sonuç' })}
                            style={{ float: 'right' }}
                        >
                            Tam Ekran
                        </Button>
                    </div>
                    <iframe
                        ref={iframeRef}
                        srcDoc={finalHtml}
                        sandbox="allow-scripts"
                        className="ai-chat-result-iframe"
                        title="AI Result"
                    />
                </div>
            );
        } catch (e) {
            return <Text type="danger">Sonuç görüntülenemedi</Text>;
        }
    };

    const renderMessage = (msg, index) => {
        if (msg.type === 'user') {
            return (
                <div key={index} className="ai-chat-message user">
                    <div className="ai-chat-message-content">
                        <Text>{msg.content}</Text>
                    </div>
                </div>
            );
        }

        const content = msg.content;
        const isError = !msg.isSuccessful || content.errorMessage;

        return (
            <div key={index} className="ai-chat-message ai">
                <div className="ai-chat-message-header">
                    <RobotOutlined />
                    {msg.isSuccessful ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 8 }} />
                    ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 8 }} />
                    )}
                    {content.chatId && (
                        <Tooltip title={t('Tekrar Çalıştır')}>
                            <Button 
                                type="text" 
                                size="small" 
                                icon={<ReloadOutlined />}
                                onClick={() => handleRerun(content.chatId)}
                                style={{ marginLeft: 8 }}
                            />
                        </Tooltip>
                    )}
                </div>
                <div className="ai-chat-message-content">
                    {isError ? (
                        <Text type="danger">{content.errorMessage || 'Bir hata oluştu'}</Text>
                    ) : (
                        <>
                            {content.sqlQuery && (
                                <Collapse 
                                    size="small" 
                                    defaultActiveKey={[]} 
                                    className="ai-chat-sql-collapse"
                                    items={[{
                                        key: 'sql',
                                        label: <Text strong>SQL Sorgu</Text>,
                                        children: <pre>{content.sqlQuery}</pre>
                                    }]}
                                />
                            )}
                            {renderHtmlResult(content.htmlTemplate, content.resultJson)}
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <Drawer
                title={
                    <div className="ai-chat-header">
                        <RobotOutlined style={{ marginRight: 8 }} />
                        {t('AI Asistan')}
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={handleNewSession}
                            style={{ marginLeft: 'auto' }}
                        >
                            {t('Yeni Sohbet')}
                        </Button>
                    </div>
                }
                placement="right"
                width={600}
                onClose={onClose}
                visible={visible}
                className="ai-chat-drawer"
                bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
            >
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    className="ai-chat-tabs"
                >
                    <TabPane 
                        tab={<span><MessageOutlined /> {t('Sohbet')}</span>} 
                        key="chat"
                    >
                        <div className="ai-chat-messages">
                            {messages.length === 0 ? (
                                <Empty 
                                    description={currentSessionId ? t('Bu sohbette henüz mesaj yok') : t('Bir soru sorarak başlayın veya sohbet seçin')}
                                    className="ai-chat-empty"
                                />
                            ) : (
                                messages.map((msg, index) => renderMessage(msg, index))
                            )}
                            {loading && (
                                <div className="ai-chat-loading">
                                    <Spin size="small" />
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                        {t('Düşünüyorum...')}
                                    </Text>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </TabPane>
                    <TabPane 
                        tab={<span><CommentOutlined /> {t('Sohbetler')}</span>} 
                        key="sessions"
                    >
                        <List
                            className="ai-chat-history"
                            dataSource={sessions}
                            renderItem={item => (
                                <List.Item 
                                    className={`ai-chat-history-item ${currentSessionId === item.id ? 'active' : ''}`}
                                    onClick={() => handleSelectSession(item)}
                                >
                                    <div className="ai-chat-history-content">
                                        <CommentOutlined style={{ color: currentSessionId === item.id ? '#1890ff' : '#666' }} />
                                        <div style={{ flex: 1, marginLeft: 8, overflow: 'hidden' }}>
                                            <Text ellipsis strong={currentSessionId === item.id}>
                                                {item.title}
                                            </Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {item.messageCount} mesaj • {new Date(item.modifiedDate).toLocaleDateString('tr-TR')}
                                            </Text>
                                        </div>
                                        <Popconfirm
                                            title={t('Bu sohbeti silmek istediğinize emin misiniz?')}
                                            onConfirm={(e) => handleDeleteSession(item.id, e)}
                                            okText={t('Evet')}
                                            cancelText={t('Hayır')}
                                            placement="left"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Popconfirm>
                                    </div>
                                </List.Item>
                            )}
                            locale={{ emptyText: t('Henüz sohbet yok') }}
                        />
                    </TabPane>
                </Tabs>

                <div className="ai-chat-input-container">
                    <TextArea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder={t('Sorunuzu yazın... (Shift+Enter ile yeni satır)')}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        disabled={loading}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSend}
                        loading={loading}
                        className="ai-chat-send-btn"
                    >
                        {t('Gönder')}
                    </Button>
                </div>
            </Drawer>
            <Modal
                title={fullscreenModal.title}
                open={fullscreenModal.visible}
                onCancel={() => setFullscreenModal({ visible: false, html: '', title: '' })}
                width="100vw"
                style={{ top: 0, padding: 0 }}
                bodyStyle={{ height: '100vh', padding: 0 }}
                footer={null}
                destroyOnClose
            >
                <iframe
                    srcDoc={fullscreenModal.html}
                    sandbox="allow-scripts"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Fullscreen AI Result"
                />
            </Modal>
        </>
    );
}
